import type { ICmsAhuDoc } from 'models';

/**
 * Phase 6e1 — typed accessors over the big CMS doc (§20.1 #7). The doc is
 * loosely typed (`[key: string]: unknown`); panels read these few paths.
 */

export interface CmsNamedRow {
  id: number;
  name?: string;
  label?: string | null;
  value?: string | number | null;
  description?: string | null;
  [key: string]: unknown;
}

function asArray(value: unknown): CmsNamedRow[] {
  return Array.isArray(value) ? (value as CmsNamedRow[]) : [];
}

function asValues(value: unknown): Array<string | number> {
  return Array.isArray(value) ? (value as Array<string | number>) : [];
}

export const cmsWidthValues = (cms: ICmsAhuDoc | undefined) =>
  asValues((cms?.dimensions as Record<string, unknown> | undefined)?.width_value_names);

export const cmsHeightValues = (cms: ICmsAhuDoc | undefined) =>
  asValues((cms?.dimensions as Record<string, unknown> | undefined)?.height_value_names);

export const cmsLengthValues = (cms: ICmsAhuDoc | undefined) =>
  asValues((cms?.dimensions as Record<string, unknown> | undefined)?.length_value_names);

export const cmsWeatherRatings = (cms: ICmsAhuDoc | undefined) =>
  asArray((cms as Record<string, unknown> | undefined)?.weather_rating);

export const cmsAirSystems = (cms: ICmsAhuDoc | undefined) =>
  asArray((cms?.air as Record<string, unknown> | undefined)?.air_system);

export const cmsSaVolumeControls = (cms: ICmsAhuDoc | undefined) =>
  asArray(
    (cms?.air as Record<string, unknown> | undefined)?.supply_air_volume_controls
  );

// ---------------------------------------------------------------------------
// Dev-sync J6 — the season masters (survey §A3b/§C4). Options are rendered
// from the CMS payload rows (resolution by `value`, never hardcoded ids).
// ---------------------------------------------------------------------------

/** `cms.air.season` — the full MasterAHUSeason list. */
export const cmsSeasons = (cms: ICmsAhuDoc | undefined) =>
  asArray((cms?.air as Record<string, unknown> | undefined)?.season);

/** `cms.air.season_by_component[<Component>]` — the per-component allowed
 * seasons (MasterAHUSeasonCA), keyed by the master component NAME, e.g.
 * 'Plenum' / 'Eliminator' / 'Gas Heater' / 'Evap Pack'. */
export const cmsSeasonsByComponent = (
  cms: ICmsAhuDoc | undefined,
  component: string
) =>
  asArray(
    (
      (cms?.air as Record<string, unknown> | undefined)?.season_by_component as
        | Record<string, unknown>
        | undefined
    )?.[component]
  );

/**
 * section.season_value derived inline from season_id + the CMS list (survey
 * §C5 item 2 — replaces BOTH legacy client recompute handlers,
 * tab.section.js:602-623 and component_view.js:1144-1159). Fallback
 * 'summer_winter' matches AHUSection.season_value's own unset default.
 */
export const deriveSeasonValue = (
  cms: ICmsAhuDoc | undefined,
  seasonId: number | string | null | undefined
): string => {
  const id = Number(seasonId) || null;
  const picked = cmsSeasons(cms).find((s) => Number(s.id) === id);
  return picked && typeof picked.value === 'string' ? picked.value : 'summer_winter';
};

export const cmsConfigurations = (cms: ICmsAhuDoc | undefined) =>
  asArray(cms?.configurations);

export const cmsCertifications = (cms: ICmsAhuDoc | undefined) =>
  asArray(cms?.certifications);

export const cmsBaseList = (cms: ICmsAhuDoc | undefined) => asArray(cms?.base);

export const cmsDmountPlanes = (cms: ICmsAhuDoc | undefined) =>
  asArray((cms?.dmounts as Record<string, unknown> | undefined)?.plane);

export const cmsDmounts = (cms: ICmsAhuDoc | undefined) =>
  (cms?.dmounts ?? {}) as Record<string, unknown>;

export const cmsSplitSections = (cms: ICmsAhuDoc | undefined) =>
  (cms?.split_sections ?? {}) as Record<string, unknown>;

export const cmsTunnelIndexes = (cms: ICmsAhuDoc | undefined) =>
  asArray(cms?.tunnels?.indexes);

/**
 * Legacy fn/map.js `fn['tunnel.configurations']` MUTATES the CMS rows with a
 * derived display name before resolving:
 *   `config_name = name.replace(' x ', ' W x ') + ' H'` ("1 x 1" → "1 W x 1 H")
 * — the insert popup's RESULTING AHU CONFIGURATION column renders
 * `resulting_configuration.config_name`. Derive (don't mutate) here.
 */
export const cmsTunnelConfigurations = (cms: ICmsAhuDoc | undefined) =>
  asArray(cms?.tunnels?.configurations).map((row) => ({
    ...row,
    config_name: `${String(row.name ?? '').replace(' x ', ' W x ')} H`,
  }));
